import React, { useEffect, useRef, useState } from 'react';
import { api, API_BASE } from '@/lib/api';
import { tokenStore } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mic, Square, Loader2, Sparkles, Volume2, Play, Pause, User as UserIcon, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';

/**
 * Voice Clinic Agent — mic record → STT → Claude Sonnet → TTS → playback.
 * Also supports typed TTS preview for any text.
 */
export default function VoiceAgentPage() {
  const { user } = useAuth();
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState(() => localStorage.getItem('voice.voiceId') || 'EXAVITQu4vr4xnSDxMaL');
  const [speak, setSpeak] = useState(true);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [ttsText, setTtsText] = useState('');
  const [ttsBusy, setTtsBusy] = useState(false);
  const [previewAudio, setPreviewAudio] = useState(null);
  const sessionRef = useRef(`voice-${Date.now()}`);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get('/voice/voices').then(r => {
      const list = r.data?.voices || [];
      setVoices(list);
    }).catch(e => toast.error(e?.response?.data?.detail || 'Could not load voices'));
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => { localStorage.setItem('voice.voiceId', voiceId); }, [voiceId]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        if (!blob.size) return;
        await sendAudio(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error('Microphone permission denied');
    }
  };

  const stopRecording = () => {
    try { mediaRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const sendAudio = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('audio_file', blob, 'speech.webm');
      fd.append('session_id', sessionRef.current);
      fd.append('voice_id', voiceId);
      fd.append('speak', speak ? 'true' : 'false');

      const token = tokenStore.get();
      const resp = await fetch(`${API_BASE}/voice/agent-chat/audio`, {
        method: 'POST', body: fd,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setMessages(m => [...m,
        { role: 'user', content: data.transcript, lang: data.language },
        { role: 'assistant', content: data.reply, tool_used: data.tool_used,
          audio_url: data.audio_url, latency_ms: data.latency_ms },
      ]);
      if (data.audio_url) {
        setTimeout(() => { try { new Audio(data.audio_url).play(); } catch {} }, 100);
      }
    } catch (e) {
      toast.error(e.message || 'Voice chat failed');
    } finally {
      setBusy(false);
    }
  };

  const generatePreview = async () => {
    const text = ttsText.trim();
    if (!text) return;
    setTtsBusy(true);
    try {
      const { data } = await api.post('/voice/tts', { text, voice_id: voiceId });
      setPreviewAudio(data.audio_url);
      try { new Audio(data.audio_url).play(); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'TTS failed');
    } finally {
      setTtsBusy(false);
    }
  };

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col bg-stone-50" data-testid="voice-agent-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-6 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shadow-sm">
          <Mic className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-bold text-stone-900 text-sm">HearClear Voice Agent</div>
          <div className="text-[11px] text-stone-500">Speak to the AI · Powered by ElevenLabs + Claude Sonnet</div>
        </div>
        <Badge variant="outline" className="text-[10px] font-mono">{user?.role}</Badge>
      </div>

      {/* Controls */}
      <div className="bg-white border-b border-stone-200 px-6 py-2.5 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs text-stone-600">Voice:</span>
          <Select value={voiceId} onValueChange={setVoiceId}>
            <SelectTrigger className="h-8 w-56 text-xs" data-testid="voice-select">
              <SelectValue placeholder="Pick a voice" />
            </SelectTrigger>
            <SelectContent>
              {voices.map(v => (
                <SelectItem key={v.voice_id} value={v.voice_id} className="text-xs">
                  {v.name} <span className="text-stone-400 ml-1">· {v.category}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-1.5 text-xs text-stone-700 cursor-pointer">
          <input type="checkbox" checked={speak} onChange={(e) => setSpeak(e.target.checked)}
                 data-testid="voice-speak-toggle" className="rounded" />
          Speak replies aloud
        </label>
        <div className="flex-1" />
        <Badge variant="outline" className="text-[10px]">{voices.length} voices loaded</Badge>
      </div>

      {/* Transcript */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-3xl mx-auto space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-10">
              <div className="inline-flex w-14 h-14 rounded-full bg-gradient-to-br from-rose-100 to-orange-100 items-center justify-center mb-3">
                <Mic className="w-7 h-7 text-rose-600" />
              </div>
              <div className="font-heading text-lg font-bold text-stone-800">Hold the mic and speak</div>
              <div className="text-sm text-stone-500 mt-1 max-w-md mx-auto">
                Try: "How many patients on trial?", "List today's follow-ups", or "Dry run a broadcast to eligible patients".
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2.5 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === 'user' ? 'bg-stone-900 text-white rounded-br-sm' : 'bg-white border border-stone-200 text-stone-800 rounded-bl-sm shadow-sm'}`}>
                <div className="whitespace-pre-wrap">{m.content}</div>
                {m.audio_url && (
                  <button
                    onClick={() => { try { new Audio(m.audio_url).play(); } catch {} }}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] text-rose-600 hover:text-rose-700"
                    data-testid={`voice-replay-${i}`}>
                    <Play className="w-3 h-3" /> Replay
                  </button>
                )}
                {m.tool_used && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-rose-600">
                    <Zap className="w-2.5 h-2.5" /> {m.tool_used} · {m.latency_ms}ms
                  </div>
                )}
                {m.lang && (
                  <div className="mt-1 text-[10px] text-stone-400">lang: {m.lang}</div>
                )}
              </div>
              {m.role === 'user' && (
                <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center shrink-0 mt-0.5">
                  <UserIcon className="w-3.5 h-3.5 text-stone-600" />
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="flex gap-2.5">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shrink-0 mt-0.5">
                <Sparkles className="w-3.5 h-3.5 text-white" />
              </div>
              <div className="bg-white border border-stone-200 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm">
                <Loader2 className="w-4 h-4 animate-spin text-stone-400" />
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Mic + TTS Preview Row */}
      <div className="bg-white border-t border-stone-200 px-6 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          {/* Big mic button */}
          <Button
            onClick={recording ? stopRecording : startRecording}
            disabled={busy}
            className={`h-14 w-14 rounded-full shrink-0 ${recording ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-rose-600 hover:bg-rose-700'} text-white`}
            data-testid="voice-mic-button"
            title={recording ? 'Stop' : 'Hold to record'}
          >
            {recording ? <Square className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </Button>

          <div className="flex-1">
            <Textarea
              value={ttsText}
              onChange={e => setTtsText(e.target.value)}
              placeholder="Or type any text to preview TTS in the selected voice…"
              className="min-h-[48px] max-h-24 text-sm resize-none"
              data-testid="voice-tts-input"
            />
          </div>
          <Button
            onClick={generatePreview}
            disabled={ttsBusy || !ttsText.trim()}
            variant="outline"
            className="h-10 shrink-0"
            data-testid="voice-tts-preview"
          >
            {ttsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Volume2 className="w-4 h-4 mr-1.5" />} Preview
          </Button>
        </div>
        <div className="max-w-3xl mx-auto mt-1.5 text-[10px] text-stone-400 text-center">
          {recording ? 'Recording… tap the square to stop' : 'Tap the mic to speak · Replies auto-play when "Speak" is on'}
        </div>
      </div>
    </div>
  );
}
